import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Loader2,
  Check,
  ChevronRight,
  ChevronLeft,
  Video,
  ClipboardList,
  CalendarCheck,
  Eye,
  Plus,
  Trash2,
  Link2,
  MapPin,
  Clock,
  Mail,
} from 'lucide-react';
import type { Application } from '@core/types';
import type { ApplicationStatus } from '@core/enums';
import { isApplicationStatusConflictError } from '@core/supabase/applicationStatusConflict';
import { applicationService } from '@core/supabase/services/applicationService';
import {
  aiInterviewSessionService,
  type AiInterviewSessionType,
} from '@core/supabase/services/aiInterviewSessionService';
import {
  interviewEventService,
  type SchedulingProvider,
} from '@core/supabase/services/interviewEventService';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { AI_INTERVIEW_LANGUAGES } from '@modules/public/ai-interview/interviewPrepConstants';
import {
  defaultInterviewWizardDraft,
  formatDaySeedForDatetimeLocal,
  type InterviewWizardDraft,
} from '@modules/interviews/interviewWizardDraft';

const BRAND = '#8B80F9';

const STEP_CONFIG = [
  { label: 'Application', icon: ClipboardList, description: 'Select the pipeline application' },
  { label: 'Scheduling', icon: CalendarCheck, description: 'Panel, availability & slot' },
  { label: 'Review', icon: Eye, description: 'Confirm & create' },
] as const;

const TOTAL = STEP_CONFIG.length;

const STEP_TIPS: string[][] = [
  [
    'Choose the candidate-job pair this AI interview belongs to.',
    'Only applications in your pipeline are listed.',
    'The prep link opens the browser AI interview room.',
  ],
  [
    'Lead interviewer details appear on confirmation emails (tracked queue).',
    'Self-book + AI prep (auto): the booking URL is your secure prep link — no paste needed.',
    'Fixed slot: sets pipeline calendar time; external self-book: paste Luma/Calendly if you prefer.',
  ],
  [
    'Creates a pipeline interview row (calendar) and an AI prep session linked to it.',
    'Candidate invite uses the same tracked email flow as Pipeline interviews.',
    'You can send another tracked email from the session drill-down any time.',
  ],
];

function pipelineForScheduledRound(round: number): ApplicationStatus {
  return round >= 2 ? 'interview_2_scheduled' : 'interview_1_scheduled';
}

function appLabel(app: Application): string {
  const c = app.candidate?.full_name ?? 'Candidate';
  const j = app.job?.title ?? 'Job';
  const cl = app.client?.name ?? 'Client';
  return `${c} → ${j} (${cl})`;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl overflow-hidden border vscode-border">
      <div
        className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 vscode-border border-b"
        style={{ color: BRAND }}
      >
        <div className="w-1 h-4 rounded-full shrink-0" style={{ background: BRAND }} />
        {title}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function FieldRow({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 vscode-text-primary">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      {children}
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string | null | undefined;
  recruiterName: string | null | undefined;
  orgUserId: string | null | undefined;
  applications: Application[];
  scheduleDayKey?: string | null;
  initialApplicationId?: string | null;
  onScheduled: () => void;
};

/**
 * Data → AI interviews → Interview: 3 steps (Application, Scheduling, Review).
 * Mirrors Pipeline interview wizard behavior; creates calendar row + linked AI prep session.
 */
export function AiInterviewScheduleWizardModal({
  open,
  onClose,
  orgId,
  orgName,
  recruiterName,
  orgUserId,
  applications,
  scheduleDayKey,
  initialApplicationId,
  onScheduled,
}: Props) {
  const [draft, setDraft] = useState<InterviewWizardDraft>(defaultInterviewWizardDraft);
  const [aiSessionType, setAiSessionType] = useState<AiInterviewSessionType>('screening');
  const [aiLanguage, setAiLanguage] = useState('en-IN');
  /** Self-book only: use generated /interview/prep/:token URL vs pasted external scheduler. */
  const [bookingLinkSource, setBookingLinkSource] = useState<'auto_ai_prep' | 'external'>('auto_ai_prep');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBookingLinkSource('auto_ai_prep');
    const base = defaultInterviewWizardDraft();
    if (initialApplicationId) base.applicationId = initialApplicationId;
    if (scheduleDayKey && /^\d{4}-\d{2}-\d{2}$/.test(scheduleDayKey)) {
      const [yy, mm, dd] = scheduleDayKey.split('-').map(Number);
      const dayDate = new Date(yy, mm - 1, dd, 9, 0, 0, 0);
      setDraft({ ...base, mode: 'fixed', fixedWhen: formatDaySeedForDatetimeLocal(dayDate), step: 0 });
    } else {
      setDraft({ ...base, step: 0 });
    }
    setError(null);
  }, [open, scheduleDayKey, initialApplicationId]);

  const updateDraft = useCallback((patch: Partial<InterviewWizardDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const app = applications.find((a) => a.id === draft.applicationId);

  const validateStep = (step: number): string | null => {
    if (step === 0 && !draft.applicationId) return 'Select an application to continue.';
    if (step === 1) {
      if (!draft.interviewerName.trim() && !draft.interviewerEmail.trim()) {
        return 'Enter at least a lead panelist name or email.';
      }
      if (draft.mode === 'self_book' && bookingLinkSource === 'external' && !draft.bookingUrl.trim()) {
        return 'Paste an external booking URL, or switch to AI prep (auto).';
      }
      if (draft.mode === 'fixed') {
        if (!draft.fixedWhen.trim()) return 'Choose date & time for the interview.';
        if (!draft.fixedLocation.trim()) return 'Enter location or video link.';
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(draft.step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    if (draft.step < TOTAL - 1) {
      updateDraft({ step: draft.step + 1 });
    }
  };

  const goBack = () => {
    setError(null);
    if (draft.step > 0) updateDraft({ step: draft.step - 1 });
  };

  const submit = async () => {
    const err = validateStep(1);
    if (err) {
      setError(err);
      return;
    }
    if (!app?.candidate_id) {
      setError('Application not found.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const panelists = draft.panelists
        .filter((p) => p.name.trim())
        .map((p) => ({ name: p.name.trim(), email: p.email.trim() || undefined, role: 'panelist' as const }));
      const scheduling_meta: Record<string, string> = {};
      if (draft.candidateAvailability.trim()) scheduling_meta.candidateAvailability = draft.candidateAvailability.trim();
      if (draft.panelAvailability.trim()) scheduling_meta.panelAvailability = draft.panelAvailability.trim();

      if (draft.mode === 'fixed') {
        const iso = new Date(draft.fixedWhen).toISOString();
        const dur = Number(draft.fixedDuration) || 60;
        const ev = await interviewEventService.create(
          {
            org_id: orgId,
            application_id: draft.applicationId,
            round: draft.round,
            scheduled_at: iso,
            location: draft.fixedLocation.trim(),
            duration_minutes: dur,
            interviewer_name: draft.interviewerName.trim() || null,
            interviewer_email: draft.interviewerEmail.trim() || null,
            notes: draft.notes.trim() || null,
            attendees: panelists,
            ...(Object.keys(scheduling_meta).length > 0 ? { scheduling_meta } : {}),
          },
          {
            candidateName: app.candidate?.full_name,
            candidateEmail: app.candidate?.email ?? null,
            jobTitle: app.job?.title,
            clientName: app.client?.name,
            recruiterName: recruiterName ?? undefined,
            orgName: orgName ?? undefined,
            createdBy: recruiterName ?? null,
          }
        );
        await applicationService.updateStatus(
          draft.applicationId,
          pipelineForScheduledRound(draft.round),
          `AI interview round ${draft.round} scheduled (hub wizard)`,
          undefined,
          undefined,
          app ? { expectedUpdatedAt: app.updated_at } : undefined
        );
        await aiInterviewSessionService.createSession({
          orgId,
          candidateId: app.candidate_id,
          jobId: app.job_id ?? null,
          applicationId: draft.applicationId,
          language: aiLanguage,
          sessionType: aiSessionType,
          createdByOrgUserId: orgUserId ?? null,
          pipelineInterviewEventId: ev.id,
        });
      } else if (bookingLinkSource === 'auto_ai_prep') {
        const created = await aiInterviewSessionService.createSession({
          orgId,
          candidateId: app.candidate_id,
          jobId: app.job_id ?? null,
          applicationId: draft.applicationId,
          language: aiLanguage,
          sessionType: aiSessionType,
          createdByOrgUserId: orgUserId ?? null,
          pipelineInterviewEventId: null,
        });
        const prepUrl = `${window.location.origin}/interview/prep/${created.access_token}`;
        const ev = await interviewEventService.createLifecycleEntry({
          org_id: orgId,
          application_id: draft.applicationId,
          round: draft.round,
          scheduling_provider: 'manual',
          scheduling_booking_url: prepUrl,
          notes: draft.notes.trim() || null,
          interviewer_name: draft.interviewerName.trim() || null,
          interviewer_email: draft.interviewerEmail.trim() || null,
          attendees: panelists,
          ...(Object.keys(scheduling_meta).length > 0 ? { scheduling_meta } : {}),
        });
        await aiInterviewSessionService.setPipelineInterviewEventId(orgId, created.id, ev.id);
      } else {
        const ev = await interviewEventService.createLifecycleEntry({
          org_id: orgId,
          application_id: draft.applicationId,
          round: draft.round,
          scheduling_provider: (draft.provider as SchedulingProvider) || null,
          scheduling_booking_url: draft.bookingUrl.trim(),
          notes: draft.notes.trim() || null,
          interviewer_name: draft.interviewerName.trim() || null,
          interviewer_email: draft.interviewerEmail.trim() || null,
          attendees: panelists,
          ...(Object.keys(scheduling_meta).length > 0 ? { scheduling_meta } : {}),
        });
        await aiInterviewSessionService.createSession({
          orgId,
          candidateId: app.candidate_id,
          jobId: app.job_id ?? null,
          applicationId: draft.applicationId,
          language: aiLanguage,
          sessionType: aiSessionType,
          createdByOrgUserId: orgUserId ?? null,
          pipelineInterviewEventId: ev.id,
        });
      }

      onScheduled();
      onClose();
    } catch (e) {
      if (isApplicationStatusConflictError(e)) {
        setError(`${e.message} Close the wizard and reopen to load the latest application state.`);
      } else {
        setError(e instanceof Error ? e.message : 'Could not schedule.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const currentStep = STEP_CONFIG[draft.step];
  const CurrentStepIcon = currentStep.icon;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl"
        style={{ border: `1px solid ${BRAND}30`, background: 'var(--vscode-bg-secondary)' }}
      >
        <div
          className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)` }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/20 shrink-0">
              <Video className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-white leading-tight">Schedule Interview</h2>
              <p className="text-[11px] text-white/75">{currentStep.description}</p>
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors shrink-0"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 shrink-0 border-b vscode-border" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center">
            {STEP_CONFIG.map((s, i) => {
              const Icon = s.icon;
              const done = i < draft.step;
              const active = i === draft.step;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-all shrink-0"
                      style={
                        done
                          ? { background: BRAND, color: '#fff', boxShadow: `0 2px 8px ${BRAND}55` }
                          : active
                            ? {
                                background: `linear-gradient(135deg, ${BRAND} 0%, #6c63ff 100%)`,
                                color: '#fff',
                                boxShadow: `0 0 0 4px ${BRAND}28, 0 2px 10px ${BRAND}40`,
                              }
                            : {
                                background: 'var(--vscode-bg-tertiary)',
                                color: 'var(--vscode-text-tertiary)',
                                border: '1.5px solid var(--vscode-border-primary)',
                              }
                      }
                    >
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" style={{ opacity: active ? 1 : 0.5 }} />}
                    </div>
                    <span
                      className="text-[10px] font-semibold whitespace-nowrap hidden sm:block"
                      style={{ color: active ? BRAND : done ? `${BRAND}cc` : 'var(--vscode-text-tertiary)' }}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < TOTAL - 1 && (
                    <div
                      className="flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all duration-300"
                      style={{
                        background: done ? `linear-gradient(90deg, ${BRAND}, ${BRAND}99)` : 'var(--vscode-border-primary)',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="grid grid-cols-[1fr_220px] gap-0 p-0">
            <div className="space-y-4 min-w-0 p-5 border-r vscode-border">
              {error && (
                <div className="text-xs rounded-lg px-3 py-2.5 flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-400">
                  <span className="font-medium shrink-0">Error:</span> {error}
                </div>
              )}

              {scheduleDayKey && (
                <div className="text-xs rounded-lg px-3 py-2.5 vscode-border border" style={{ borderLeft: `3px solid ${BRAND}` }}>
                  <span className="font-semibold" style={{ color: BRAND }}>
                    From calendar:
                  </span>{' '}
                  <span className="vscode-text-secondary">
                    Fixed slot pre-set for{' '}
                    {formatDateTimeGmt(new Date(`${scheduleDayKey}T12:00:00`).toISOString())}. Adjust exact time in Scheduling.
                  </span>
                </div>
              )}

              {draft.step === 0 && (
                <SectionCard title="Choose Application">
                  <p className="text-xs vscode-text-secondary">Select the candidate-job pair this AI interview belongs to.</p>
                  {applications.length === 0 && (
                    <div className="text-xs rounded-lg px-3 py-2 border vscode-border text-amber-500" style={{ borderLeft: '3px solid #f59e0b' }}>
                      No applications found — create a pipeline application first.
                    </div>
                  )}
                  <FieldRow label="Application" required>
                    <select
                      className="input w-full text-sm"
                      value={draft.applicationId}
                      onChange={(e) => updateDraft({ applicationId: e.target.value })}
                      disabled={Boolean(initialApplicationId)}
                    >
                      <option value="">Select application…</option>
                      {applications.map((a) => (
                        <option key={a.id} value={a.id}>
                          {appLabel(a)}
                        </option>
                      ))}
                    </select>
                  </FieldRow>
                  {app && (
                    <div className="rounded-lg p-3 space-y-1 border vscode-border" style={{ borderLeft: `3px solid ${BRAND}` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BRAND }}>
                        Selected
                      </p>
                      <p className="text-sm font-semibold vscode-text-primary">
                        {app.candidate?.full_name} → {app.job?.title}
                      </p>
                      <p className="text-xs vscode-text-secondary">
                        {app.client?.name} · {app.status.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                </SectionCard>
              )}

              {draft.step === 1 && (
                <>
                  <SectionCard title="AI prep session">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <FieldRow label="Prep language">
                        <select className="input w-full text-sm" value={aiLanguage} onChange={(e) => setAiLanguage(e.target.value)}>
                          {AI_INTERVIEW_LANGUAGES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </FieldRow>
                      <FieldRow label="AI round type">
                        <select
                          className="input w-full text-sm"
                          value={aiSessionType}
                          onChange={(e) => setAiSessionType(e.target.value as AiInterviewSessionType)}
                        >
                          <option value="screening">Screening</option>
                          <option value="technical">Technical</option>
                          <option value="behavioral">Behavioral</option>
                        </select>
                      </FieldRow>
                    </div>
                  </SectionCard>

                  <SectionCard title="Lead interviewer">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <FieldRow label="Name">
                        <input
                          className="input w-full text-sm"
                          value={draft.interviewerName}
                          onChange={(e) => updateDraft({ interviewerName: e.target.value })}
                          placeholder="e.g. Priya Nair"
                        />
                      </FieldRow>
                      <FieldRow label="Email">
                        <input
                          type="email"
                          className="input w-full text-sm"
                          value={draft.interviewerEmail}
                          onChange={(e) => updateDraft({ interviewerEmail: e.target.value })}
                          placeholder="hm@client.com"
                        />
                      </FieldRow>
                    </div>
                  </SectionCard>

                  <SectionCard title="Round & panelists">
                    <FieldRow label="Pipeline round #">
                      <input
                        type="number"
                        min={1}
                        className="input w-28 text-sm"
                        value={draft.round}
                        onChange={(e) => updateDraft({ round: Math.max(1, Number(e.target.value) || 1) })}
                      />
                    </FieldRow>
                    <div>
                      <label className="block text-xs font-medium mb-2 vscode-text-primary">Additional panelists</label>
                      <div className="space-y-2">
                        {draft.panelists.map((row, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              className="input flex-1 text-sm"
                              placeholder="Name"
                              value={row.name}
                              onChange={(e) => {
                                const next = [...draft.panelists];
                                next[idx] = { ...next[idx], name: e.target.value };
                                updateDraft({ panelists: next });
                              }}
                            />
                            <input
                              className="input flex-1 text-sm"
                              placeholder="Email (optional)"
                              value={row.email}
                              onChange={(e) => {
                                const next = [...draft.panelists];
                                next[idx] = { ...next[idx], email: e.target.value };
                                updateDraft({ panelists: next });
                              }}
                            />
                            {draft.panelists.length > 1 && (
                              <button
                                type="button"
                                className="p-1.5 rounded text-red-400 opacity-70 hover:opacity-100 transition-opacity"
                                onClick={() => updateDraft({ panelists: draft.panelists.filter((_, i) => i !== idx) })}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="mt-2 flex items-center gap-1 text-xs font-medium"
                        style={{ color: BRAND }}
                        onClick={() => updateDraft({ panelists: [...draft.panelists, { name: '', email: '' }] })}
                      >
                        <Plus className="w-3.5 h-3.5" /> Add panelist
                      </button>
                    </div>
                  </SectionCard>

                  <SectionCard title="Availability (optional)">
                    <FieldRow label="Candidate">
                      <textarea
                        className="input w-full text-sm min-h-[72px]"
                        value={draft.candidateAvailability}
                        onChange={(e) => updateDraft({ candidateAvailability: e.target.value })}
                        placeholder="e.g. Weekdays after 6 pm IST"
                      />
                    </FieldRow>
                    <FieldRow label="Panel">
                      <textarea
                        className="input w-full text-sm min-h-[72px]"
                        value={draft.panelAvailability}
                        onChange={(e) => updateDraft({ panelAvailability: e.target.value })}
                        placeholder="e.g. Tue–Thu 2–5 pm"
                      />
                    </FieldRow>
                  </SectionCard>

                  <SectionCard title="Scheduling mode">
                    <div className="flex gap-2">
                      {(['self_book', 'fixed'] as const).map((mode) => {
                        const active = draft.mode === mode;
                        return (
                          <button
                            key={mode}
                            type="button"
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium border transition-all"
                            style={
                              active
                                ? { background: `${BRAND}20`, borderColor: BRAND, color: BRAND }
                                : {
                                    background: 'var(--vscode-bg-tertiary)',
                                    borderColor: 'var(--vscode-border-primary)',
                                    color: 'var(--vscode-text-secondary)',
                                  }
                            }
                            onClick={() => updateDraft({ mode })}
                          >
                            {mode === 'self_book' ? (
                              <>
                                <Link2 className="w-3.5 h-3.5" /> Self-book
                              </>
                            ) : (
                              <>
                                <Clock className="w-3.5 h-3.5" /> Fixed slot
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </SectionCard>

                  {draft.mode === 'self_book' ? (
                    <SectionCard title="Booking link">
                      <p className="text-xs vscode-text-secondary mb-2">
                        Choose how the candidate gets a link for this round. <strong className="text-[var(--vscode-foreground)]">AI prep (auto)</strong> is recommended — the URL is created when you save (same link you can paste into tracked email).
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          type="button"
                          className={`flex-1 min-w-[140px] py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                            bookingLinkSource === 'auto_ai_prep'
                              ? 'border-[color-mix(in_srgb,var(--vscode-text-link)_55%,transparent)] bg-[color-mix(in_srgb,var(--vscode-text-link)_12%,transparent)]'
                              : 'border-[var(--vscode-border-primary)] vscode-text-secondary'
                          }`}
                          onClick={() => setBookingLinkSource('auto_ai_prep')}
                        >
                          AI prep (auto)
                        </button>
                        <button
                          type="button"
                          className={`flex-1 min-w-[140px] py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                            bookingLinkSource === 'external'
                              ? 'border-[color-mix(in_srgb,var(--vscode-text-link)_55%,transparent)] bg-[color-mix(in_srgb,var(--vscode-text-link)_12%,transparent)]'
                              : 'border-[var(--vscode-border-primary)] vscode-text-secondary'
                          }`}
                          onClick={() => setBookingLinkSource('external')}
                        >
                          External (Luma / Calendly)
                        </button>
                      </div>
                      {bookingLinkSource === 'auto_ai_prep' ? (
                        <div className="rounded-lg border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] px-3 py-2.5 text-xs vscode-text-secondary space-y-1">
                          <p>
                            On <strong className="text-[var(--vscode-foreground)]">Create</strong>, we generate the secure prep URL and store it on the pipeline calendar row as the booking link. Send it from{' '}
                            <strong className="text-[var(--vscode-foreground)]">Email</strong> on the session for tracking pixels / follow-ups.
                          </p>
                          <p className="vscode-text-tertiary font-mono break-all opacity-90">
                            {typeof window !== 'undefined' ? window.location.origin : ''}/interview/prep/[token after save]
                          </p>
                        </div>
                      ) : (
                        <>
                          <FieldRow label="Scheduling tool">
                            <select
                              className="input w-full text-sm"
                              value={draft.provider}
                              onChange={(e) => updateDraft({ provider: e.target.value as SchedulingProvider | '' })}
                            >
                              <option value="">Other / unspecified</option>
                              <option value="luma">Luma</option>
                              <option value="calendly">Calendly</option>
                              <option value="manual">Manual link</option>
                            </select>
                          </FieldRow>
                          <FieldRow label="Booking page URL" required>
                            <input
                              className="input w-full text-sm"
                              value={draft.bookingUrl}
                              onChange={(e) => updateDraft({ bookingUrl: e.target.value })}
                              placeholder="https://…"
                            />
                          </FieldRow>
                        </>
                      )}
                    </SectionCard>
                  ) : (
                    <SectionCard title="Fixed slot">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <FieldRow label="Start date & time" required>
                          <input
                            type="datetime-local"
                            className="input w-full text-sm"
                            value={draft.fixedWhen}
                            onChange={(e) => updateDraft({ fixedWhen: e.target.value })}
                          />
                        </FieldRow>
                        <FieldRow label="Duration (minutes)">
                          <input
                            type="number"
                            min={15}
                            step={5}
                            className="input w-full text-sm"
                            value={draft.fixedDuration}
                            onChange={(e) => updateDraft({ fixedDuration: e.target.value })}
                          />
                        </FieldRow>
                      </div>
                      <FieldRow label="Location / video link" required>
                        <div className="relative">
                          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 vscode-text-tertiary" />
                          <input
                            className="input w-full text-sm pl-8"
                            value={draft.fixedLocation}
                            onChange={(e) => updateDraft({ fixedLocation: e.target.value })}
                            placeholder="Google Meet / address"
                          />
                        </div>
                      </FieldRow>
                    </SectionCard>
                  )}

                  <SectionCard title="Internal notes">
                    <textarea
                      className="input w-full text-sm min-h-[60px]"
                      value={draft.notes}
                      onChange={(e) => updateDraft({ notes: e.target.value })}
                      placeholder="Context for the panel…"
                    />
                  </SectionCard>
                </>
              )}

              {draft.step === 2 && app && (
                <SectionCard title="Review">
                  <div className="space-y-3 text-sm">
                    <div className="rounded-lg p-3 space-y-1 border vscode-border" style={{ borderLeft: `3px solid ${BRAND}` }}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BRAND }}>
                        Application
                      </p>
                      <p className="font-semibold vscode-text-primary">{app.candidate?.full_name}</p>
                      <p className="text-xs vscode-text-secondary">
                        {app.job?.title} · {app.client?.name}
                      </p>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <dt className="font-medium vscode-text-secondary">AI prep</dt>
                        <dd className="vscode-text-primary capitalize">
                          {aiSessionType} · {aiLanguage}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium vscode-text-secondary">Pipeline round</dt>
                        <dd className="vscode-text-primary">{draft.round}</dd>
                      </div>
                    </dl>
                    <div
                      className="rounded-lg px-3 py-2.5 text-xs border vscode-border space-y-2"
                      style={{ borderLeft: `3px solid ${BRAND}` }}
                    >
                      <p className="font-semibold vscode-text-primary flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden />
                        Tracked email
                      </p>
                      <p className="vscode-text-secondary leading-relaxed">
                        For a <strong className="text-[var(--vscode-foreground)]">fixed slot</strong>, confirmation is queued to the candidate and panel (same queue as Pipeline interviews — opens / follow-ups in Email activity).
                      </p>
                      {draft.applicationId && (
                        <Link
                          to={`/emails/compose?applicationId=${encodeURIComponent(draft.applicationId)}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold"
                          style={{ color: BRAND }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open compose (tracked) →
                        </Link>
                      )}
                    </div>
                  </div>
                </SectionCard>
              )}
            </div>

            <div
              className="flex flex-col gap-0 sticky top-0 self-start overflow-y-auto"
              style={{ borderLeft: `3px solid ${BRAND}`, maxHeight: 'calc(92vh - 120px)' }}
            >
              <div className="p-4 space-y-3 border-b vscode-border">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: BRAND }}>
                    <CurrentStepIcon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: BRAND }}>
                    {currentStep.label}
                  </p>
                </div>
                <ul className="space-y-2.5">
                  {STEP_TIPS[draft.step].map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: BRAND }} />
                      <p className="text-[11px] leading-relaxed vscode-text-secondary">{tip}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t vscode-border" style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="h-0.5 w-full" style={{ background: 'var(--vscode-border-primary)' }}>
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{
                width: `${((draft.step + 1) / TOTAL) * 100}%`,
                background: `linear-gradient(90deg, ${BRAND}, #6c63ff)`,
              }}
            />
          </div>
          <div className="flex items-center gap-3 px-5 py-3.5">
            <button type="button" className="btn-secondary flex items-center gap-1.5 text-sm" disabled={draft.step === 0} onClick={goBack}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex-1 flex items-center justify-center gap-1.5">
              {STEP_CONFIG.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === draft.step ? 20 : 6,
                    height: 6,
                    background:
                      i < draft.step
                        ? BRAND
                        : i === draft.step
                          ? `linear-gradient(90deg, ${BRAND}, #6c63ff)`
                          : 'var(--vscode-border-primary)',
                  }}
                />
              ))}
            </div>
            <span className="text-xs vscode-text-tertiary shrink-0">
              Step {draft.step + 1} of {TOTAL}
            </span>
            {draft.step < TOTAL - 1 ? (
              <button type="button" className="btn-primary flex items-center gap-1.5 text-sm" onClick={goNext}>
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${BRAND}, #6c63ff)`, boxShadow: `0 2px 12px ${BRAND}55` }}
                disabled={submitting}
                onClick={() => void submit()}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? 'Creating…' : 'Create AI interview'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
